import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { contactInfo } from '../data';
import ScrollReveal from '../components/ScrollReveal';

// Unikt, skræddersyet indhold for hver ydelse for at gøre undersiderne forskellige og rige
const serviceCustomContent = {
  'privat-vinduespudsning': {
    subtitle: "Få mere lys og glæde ind i din bolig med krystalklare ruder",
    intro: "Et rent hjem starter med rene vinduer. Hos Ravn's Vinduespolering forstår vi, at dit hjem er din base. Derfor passer vi ekstra godt på dine ting, når vi pudser dine vinduer – både indvendigt og udvendigt. Vi medbringer altid rene sko, tørrer grundigt af efter os og efterlader ingen spor udover krystalklare ruder.",
    benefitsTitle: "Hvorfor vælge privat vinduespudsning hos Ravn's?",
    benefits: [
      {
        title: "Skånsom og grundig",
        description: "Vi vasker altid rammer og karme af, så hele vinduespartiet fremstår som nyt.",
        icon: "Sparkles"
      },
      {
        title: "Indvendig & udvendig",
        description: "Vi klarer begge dele med samme høje præcision og professionalisme.",
        icon: "Home"
      },
      {
        title: "Fleksible tider",
        description: "Vi finder en tid, der passer ind i din hverdag, så du ikke skal stresse.",
        icon: "Clock"
      }
    ],
    processTitle: "Sådan foregår det – trin for trin",
    process: [
      {
        step: "01",
        title: "Nem bestilling",
        description: "Udfyld formularen eller giv mig et kald. Jeg giver dig et uforpligtende tilbud lynhurtigt."
      },
      {
        step: "02",
        title: "Professionel pudsning",
        description: "Jeg ankommer til den aftalte tid og pudser dine vinduer med professionelt udstyr og svanemærket sæbe."
      },
      {
        step: "03",
        title: "Krystalklart resultat",
        description: "Jeg tørrer karme og sålbænke af, så alt står snorlige. Du betaler først, når du har godkendt resultatet."
      }
    ]
  },
  'erhvervspolering': {
    subtitle: "Professionel vinduespudsning til virksomheder, butikker og institutioner",
    intro: "Dine vinduer er din virksomheds ansigt udadtil. Beskidte ruder kan sende det forkerte signal til kunder og samarbejdspartnere. Vi tilbyder fleksibel og pålidelig erhvervspolering, der tilpasses jeres åbningstider og behov. Uanset om det er en lille butiksfacade eller et stort kontordomicil, leverer vi et fejlfrit resultat hver gang.",
    benefitsTitle: "Hvorfor vælge erhvervspolering hos Ravn's?",
    benefits: [
      {
        title: "Fleksibel planlægning",
        description: "Vi kan pudse før eller efter jeres åbningstid, så vi ikke forstyrrer jeres medarbejdere eller kunder.",
        icon: "Calendar"
      },
      {
        title: "EAN-fakturering & faste aftaler",
        description: "Nem og overskuelig administration med faste intervaller og elektronisk fakturering.",
        icon: "FileText"
      },
      {
        title: "Sikkerhed i top",
        description: "Vi overholder alle gældende sikkerhedskrav og bruger professionelt udstyr til opgaver i højden.",
        icon: "ShieldCheck"
      }
    ],
    processTitle: "Professionelt samarbejde – trin for trin",
    process: [
      {
        step: "01",
        title: "Behovsafklaring",
        description: "Vi gennemgår jeres lokaler og aftaler det rette interval, uanset om det er ugentligt, hver 14. dag eller månedligt."
      },
      {
        step: "02",
        title: "Effektiv udførelse",
        description: "Vores erfarne vinduespudser klarer opgaven hurtigt, diskret og med minimal forstyrrelse af jeres daglige drift."
      },
      {
        step: "03",
        title: "Nem administration",
        description: "Vi sender fakturaen elektronisk (EAN eller e-mail) efter hver polering, helt automatisk og fejlfrit."
      }
    ]
  },
  'abonnementsordning': {
    subtitle: "Den nemmeste vej til permanent rene vinduer – helt uden besvær",
    intro: "Med en abonnementsordning hos Ravn's slipper du for at bekymre dig om beskidte vinduer. Vi kommer automatisk forbi med et fast interval (f.eks. hver 4. eller 8. uge) og pudser dine ruder udvendigt. Du behøver ikke engang at være hjemme – vi klarer det hele og sender en SMS dagen før, så du ved, hvornår vi kommer.",
    benefitsTitle: "Hvorfor vælge en abonnementsordning hos Ravn's?",
    benefits: [
      {
        title: "Ingen binding",
        description: "Du kan til enhver tid ændre dit interval eller opsige dit abonnement uden gebyrer.",
        icon: "Unlock"
      },
      {
        title: "SMS-varsling",
        description: "Vi sender altid en venlig påmindelse dagen før, så du kan lukke hunden ind og låse havelågen op.",
        icon: "MessageSquare"
      },
      {
        title: "Fast lav pris",
        description: "Du får vores absolut bedste pris, når du vælger en fast abonnementsaftale.",
        icon: "TrendingDown"
      }
    ],
    processTitle: "Sådan fungerer abonnementet – trin for trin",
    process: [
      {
        step: "01",
        title: "Vælg dit interval",
        description: "Vælg om du vil have pudset hver 4. eller 8. uge. Vi rådgiver dig gerne om, hvad der passer bedst til dit hus."
      },
      {
        step: "02",
        title: "SMS dagen før",
        description: "Du modtager en SMS dagen før, så du ved, vi kommer. Du behøver ikke være hjemme, hvis vi kun skal pudse udvendigt."
      },
      {
        step: "03",
        title: "Altid rene ruder",
        description: "Vi pudser dine vinduer til perfektion. Betalingen kører automatisk og nemt via MobilePay eller bankoverførsel."
      }
    ]
  },
  'miljoerigtig-solcellevask': {
    subtitle: "Få det maksimale udbytte ud af dine solceller med professionel vask",
    intro: "Vidste du, at snavs, pollen, sod og alger på dine solceller kan reducere din strømproduktion med op til 20-25%? Almindeligt postevand indeholder kalk, som efterlader striber og skader cellerne. Vi vasker dine solceller med 100% rent, kalkfrit ultra-rent vand, som nænsomt opløser skidt uden brug af kemikalier, så dine solceller yder maksimalt igen.",
    benefitsTitle: "Hvorfor vælge miljørigtig solcellevask hos Ravn's?",
    benefits: [
      {
        title: "Kalkfrit ultra-rent vand",
        description: "Vi efterlader ingen kalkrester, hvilket sikrer en helt ren overflade og optimal lysoptagelse.",
        icon: "Droplets"
      },
      {
        title: "Ingen kemikalier",
        description: "Vi beskytter miljøet og dine solceller ved udelukkende at bruge rent, filtreret vand.",
        icon: "Leaf"
      },
      {
        title: "Øget levetid",
        description: "Regelmæssig vask forhindrer, at snavs brænder sig fast og beskadiger solcellepanelerne permanent.",
        icon: "Shield"
      }
    ],
    processTitle: "Effektiv og skånsom vask – trin for trin",
    process: [
      {
        step: "01",
        title: "Skånsom forberedelse",
        description: "Vi inspicerer solcellerne og klargør vores professionelle vaskeanlæg med ultra-rent vand."
      },
      {
        step: "02",
        title: "Blid vask uden ridser",
        description: "Vi bruger specialdesignede, bløde teleskopbørster, der nænsomt fjerner alt skidt uden at ridse glasset."
      },
      {
        step: "03",
        title: "Naturlig tørring",
        description: "Da vandet er 100% kalkfrit, tørrer panelerne helt stribefrit op af sig selv, og din strømproduktion stiger med det samme."
      }
    ]
  }
};

export default function ServiceDetailPage({ services = [], onSelectService }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Find the current service based on the slug
  const service = services.find(s => (s.slug._ || s.slug) === slug);
  const currentSlug = service ? (service.slug._ || service.slug) : '';
  const custom = serviceCustomContent[currentSlug];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  // Pre-fill the service field when the service loads
  useEffect(() => {
    if (service) {
      setFormData(prev => ({
        ...prev,
        service: service.title,
        message: `Hej Morten,\n\nJeg vil gerne have et uforpligtende tilbud på ${service.title.toLowerCase()}.\n\n[Tilføj gerne detaljer om opgaven her]`
      }));
    }
  }, [service]);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (services.length === 0) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin inline-block w-10 h-10 border-4 border-[#0e227b] border-t-transparent rounded-full" role="status">
          <span className="sr-only">Indlæser...</span>
        </div>
        <p className="mt-4 text-slate-600 font-medium">Henter ydelser...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex flex-col items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md space-y-6">
          <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto">
            <Icons.AlertTriangle className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 uppercase tracking-tight">Ydelsen blev ikke fundet</h1>
          <p className="text-slate-600">
            Beklager, men den ydelse du leder efter findes ikke eller er blevet flyttet.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#0e227b] hover:bg-[#0e227b]/90 text-white font-bold py-3 px-6 rounded-full transition-all duration-200 text-sm uppercase tracking-wider shadow-md"
          >
            <Icons.ArrowLeft className="w-4 h-4" />
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  // Helper to map string to Lucide icon
  const getIcon = (iconName, className = "w-6 h-6") => {
    const IconComponent = Icons[iconName];
    if (IconComponent) {
      return <IconComponent className={className} />;
    }
    return <Icons.Sparkles className={className} />;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        // Reset form but keep service prefilled
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: service.title,
          message: `Hej Morten,\n\nJeg vil gerne have et uforpligtende tilbud på ${service.title.toLowerCase()}.`,
          website_url: ''
        });
      } else {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse.'
      });
    }
  };

  // Get other services for the bottom section
  const otherServices = services.filter(s => s.id !== service.id).slice(0, 3);

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* Hero Banner Section */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-24 bg-[#0f126e] text-white overflow-hidden">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Back Button */}
          <Link 
            to="/#ydelser" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <Icons.ArrowLeft className="w-4 h-4" />
            Se alle ydelser
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Left Column: Title & Intro */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-tight">
                {service.title}
              </h1>

              <p className="text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed">
                {custom ? custom.subtitle : service.description}
              </p>
            </div>

            {/* Right Column: Quick Stats / Trust Badges */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-4 text-center space-y-2 shadow-md border border-slate-100/10">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-900">100% Garanti</p>
                <p className="text-[11px] text-slate-600">Fuld tilfredshed eller pengene tilbage</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center space-y-2 shadow-md border border-slate-100/10">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Miljøvenligt</p>
                <p className="text-[11px] text-slate-600">Svanemærkede og biologiske sæber</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center space-y-2 shadow-md border border-slate-100/10">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Hurtig Svar</p>
                <p className="text-[11px] text-slate-600">Tilbud inden for 24 timer</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center space-y-2 shadow-md border border-slate-100/10">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Lokal Service</p>
                <p className="text-[11px] text-slate-600">Personlig betjening i Østjylland</p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Details & Features */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* What's Included */}
              <ScrollReveal>
                <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm space-y-4">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight">
                    Hvad er inkluderet?
                  </h2>

                  <div 
                    className="prose prose-slate max-w-none text-slate-600 text-sm sm:text-base leading-relaxed
                      prose-ul:list-none prose-ul:pl-0 prose-ul:space-y-3
                      prose-li:relative prose-li:pl-7
                      prose-li:before:absolute prose-li:before:left-0 prose-li:before:top-1 prose-li:before:w-5 prose-li:before:h-5 prose-li:before:bg-emerald-100 prose-li:before:text-emerald-600 prose-li:before:rounded-full prose-li:before:flex prose-li:before:items-center prose-li:before:justify-center prose-li:before:font-bold prose-li:before:text-xs prose-li:before:content-['✓']"
                    dangerouslySetInnerHTML={{ __html: service.features }}
                  />
                </div>
              </ScrollReveal>

              {/* Custom Service Intro & Benefits */}
              {custom && (
                <div className="space-y-8 pt-4">
                  {/* Detailed Intro */}
                  <ScrollReveal delay={100}>
                    <div className="space-y-4">
                      <h3 className="text-lg font-extrabold text-slate-900 uppercase tracking-wider">
                        Om ydelsen
                      </h3>
                      <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                        {custom.intro}
                      </p>
                    </div>
                  </ScrollReveal>

                  {/* Custom Benefits Grid */}
                  <ScrollReveal delay={150}>
                    <div className="space-y-6">
                      <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight">
                        {custom.benefitsTitle}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-y-0 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                        {custom.benefits.map((benefit, idx) => (
                          <div key={idx} className="pt-6 first:pt-0 sm:pt-0 sm:px-6 first:pl-0 last:pr-0 space-y-2">
                            <h4 className="font-bold text-sm text-slate-900 uppercase tracking-wider">
                              {benefit.title}
                            </h4>
                            <p className="text-xs text-slate-500 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Custom Process Timeline */}
                  <ScrollReveal delay={200}>
                    <div className="space-y-6">
                      <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight">
                        {custom.processTitle}
                      </h3>
                      <div className="relative border-l-2 border-[#0e227b]/20 pl-6 ml-4 space-y-8">
                        {custom.process.map((step, idx) => (
                          <div key={idx} className="relative">
                            {/* Bullet */}
                            <div className="absolute -left-[35px] top-0.5 w-6 h-6 rounded-full bg-[#0e227b] text-white text-[10px] font-bold flex items-center justify-center ring-4 ring-white">
                              {step.step}
                            </div>
                            <div className="space-y-1">
                              <h4 className="font-bold text-sm text-slate-900 uppercase tracking-wider">
                                {step.title}
                              </h4>
                              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                                {step.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              )}

              {/* Ravn's Guarantees */}
              <ScrollReveal delay={250}>
                <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm space-y-6">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight">
                    Ravn's Tryghedsgaranti
                  </h2>

                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Når du vælger Ravn's Vinduespolering, vælger du tryghed og kompromisløs kvalitet. 
                    Jeg går op i mit arbejde med liv og sjæl, og jeg forlader ikke opgaven, før du er 100% tilfreds.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 pt-6 border-t border-slate-100">
                    <div className="space-y-2 pb-4 border-b border-slate-100 sm:pb-6 sm:pr-6">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-slate-900">100% Tilfredshed</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Er der en plet eller stribe, jeg har overset? Ring med det samme, og jeg retter det uden beregning.
                      </p>
                    </div>

                    <div className="space-y-2 pb-4 border-b border-slate-100 sm:pb-6 sm:pl-6 sm:border-l sm:border-slate-100">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-slate-900">Miljøvenlig Sæbe</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Jeg skåner både dine vinduer, dine planter og miljøet ved udelukkende at bruge svanemærket sæbe.
                      </p>
                    </div>

                    <div className="space-y-2 pt-4 sm:pt-6 sm:pr-6">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-slate-900">Fuld Forsikring</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Jeg passer ekstremt godt på dit hjem, men skulle uheldet være ude, er jeg fuldt ansvarsforsikret.
                      </p>
                    </div>

                    <div className="space-y-2 pt-4 sm:pt-6 sm:pl-6 sm:border-l sm:border-slate-100">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-slate-900">Pålidelige Aftaler</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        En aftale er en aftale. Jeg kommer til den aftalte tid, og giver altid besked i god tid ved ændringer.
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

            </div>

            {/* Right Column: High-converting Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-md space-y-6">
                <div className="border-b border-slate-100 pb-4">
                  <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wider">
                    Få et gratis tilbud
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Udfyld formularen, så vender jeg tilbage med et uforpligtende tilbud på {service.title.toLowerCase()} inden for 24 timer.
                  </p>
                </div>

                {status.success ? (
                  <div className="text-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto">
                      <Icons.CheckCircle className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 uppercase">Tak for din forespørgsel!</h3>
                    <p className="text-slate-600 text-xs max-w-xs mx-auto">
                      Jeg har modtaget din besked og vender tilbage til dig hurtigst muligt med et skarpt tilbud.
                    </p>
                    <button
                      onClick={() => setStatus({ submitting: false, success: false, error: null })}
                      className="mt-4 bg-[#0e227b] hover:bg-[#0e227b]/90 text-white font-semibold px-5 py-2 rounded-full transition-colors cursor-pointer text-xs uppercase tracking-wider"
                    >
                      Send en ny forespørgsel
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    
                    {/* Antispam Honeypot Field - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute -top-9999 -left-9999">
                      <label htmlFor="service_website_url">Efterlad dette felt tomt</label>
                      <input
                        id="service_website_url"
                        type="text"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label htmlFor="service_name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="service_name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-xs text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-1">
                      <label htmlFor="service_email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="service_email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="din@email.dk"
                        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-xs text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-1">
                      <label htmlFor="service_phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="service_phone"
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="F.eks. 12345678"
                        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-xs text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Address */}
                    <div className="space-y-1">
                      <label htmlFor="service_address" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Adresse / By <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="service_address"
                        type="text"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Vejnavn, husnummer, postnr. & by"
                        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-xs text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-1">
                      <label htmlFor="service_message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Besked / Detaljer <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="service_message"
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="F.eks. antal vinduer, specielle adgangsforhold..."
                        className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/10 outline-none transition-all text-xs text-slate-900 placeholder-slate-400 resize-none"
                      ></textarea>
                    </div>

                    {/* Error Message */}
                    {status.error && (
                      <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs flex items-start space-x-2">
                        <Icons.AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full bg-[#0e227b] hover:bg-[#0e227b]/90 disabled:bg-slate-300 text-white font-bold py-3 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 cursor-pointer text-xs uppercase tracking-wider"
                    >
                      {status.submitting ? (
                        <>
                          <Icons.Loader2 className="w-4 h-4 animate-spin text-white" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <Icons.Send className="w-4 h-4 text-white" />
                          <span>Få gratis tilbud</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

            </div>

          </div>

        </div>
      </section>

    </div>
  );
}
